import {
  ArrowDownOnSquareIcon,
  BookmarkIcon,
  CubeIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Pdf as PdfIcon } from '@praetorian-chariot/ui';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { MyFile, ParsedFileTypes } from '@/types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';
import { hasBinaryExtension } from '@/utils/file.util';

export function getFileType(filename: string): ParsedFileTypes {
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
  if (filename.match(imageExtensions)) {
    return ParsedFileTypes.IMAGE;
  } else if (filename.endsWith('.pdf')) {
    return ParsedFileTypes.PDF;
  } else if (hasBinaryExtension(filename)) {
    return ParsedFileTypes.BINARY;
  } else {
    return ParsedFileTypes.DOCUMENT;
  }
}

interface DocumentListItemProps {
  file: MyFile;
  onView: (file: MyFile) => void;
  onDownload: (file: MyFile) => void;
  onDelete?: (file: MyFile) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (file: MyFile) => void;
  displayName?: string;
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  file,
  onView,
  onDownload,
  onDelete,
  isFavorite = false,
  onToggleFavorite,
  displayName,
}) => {
  const fileType = getFileType(file.name);
  const name = displayName || file.name;

  return (
    <div className="hover:bg-chariot-background-secondary group flex items-center justify-between p-4 transition-colors">
      <div className="flex items-center gap-4 overflow-hidden">
        <div className="shrink-0">
          {fileType === ParsedFileTypes.IMAGE ? (
            <PhotoIcon className="text-chariot-text-secondary size-8" />
          ) : fileType === ParsedFileTypes.PDF ? (
            <PdfIcon className="text-chariot-text-secondary size-8 stroke-2" />
          ) : fileType === ParsedFileTypes.BINARY ? (
            <CubeIcon className="text-chariot-text-secondary size-8" />
          ) : (
            <DocumentIcon className="text-chariot-text-secondary size-8" />
          )}
        </div>
        <div className="flex flex-col overflow-hidden">
          <button
            className="text-chariot-text-primary truncate text-left text-sm font-medium hover:underline"
            onClick={() => onView(file)}
          >
            {name}
          </button>
          <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
            <span>Uploaded by {file.username}</span>
            <span className="text-chariot-stroke">•</span>
            <span>{formatDate(file.updated)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
        {onToggleFavorite && (
          <Tooltip
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <button
              onClick={() => onToggleFavorite(file)}
              className="hover:bg-chariot-background-tertiary rounded p-2"
            >
              <BookmarkIcon
                className={cn(
                  'size-5',
                  isFavorite
                    ? 'fill-current text-yellow-500'
                    : 'text-chariot-text-secondary'
                )}
              />
            </button>
          </Tooltip>
        )}
        <Tooltip title="Download">
          <button
            onClick={() => onDownload(file)}
            className="hover:bg-chariot-background-tertiary rounded p-2"
          >
            <ArrowDownOnSquareIcon className="text-chariot-text-secondary size-5" />
          </button>
        </Tooltip>
        {onDelete && (
          <Tooltip title="Delete">
            <button
              onClick={() => onDelete(file)}
              className="hover:bg-chariot-background-tertiary rounded p-2"
            >
              <TrashIcon className="text-chariot-text-secondary size-5" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
